import React from "react";
import { useTranslation } from "react-i18next";

interface InfoModalExample {
  emoji: string;
  title: string;
  description: string;
}

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  examples: InfoModalExample[];
  themeColor?: string;
  icon?: string;
}

export const InfoModal: React.FC<InfoModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  examples,
  themeColor = "blue",
  icon = "💡",
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex backdrop-blur-sm items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-full bg-${themeColor}-100 dark:bg-${themeColor}-900/30 flex items-center justify-center`}
              >
                <span className="text-2xl">{icon}</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {title}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div
            className={`bg-${themeColor}-50 dark:bg-${themeColor}-900/20 p-4 rounded-xl border border-${themeColor}-200 dark:border-${themeColor}-800`}
          >
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {description}
            </p>
          </div>

          {examples.length > 0 && (
            <div className="space-y-4">
              <h4
                className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 flex items-center gap-2`}
              >
                <span className="text-xl">👨‍🏫</span>
                {t("common.examples", "Examples")}
              </h4>

              <div className="grid gap-3">
                {examples.map((example, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <span className="text-lg">{example.emoji}</span>
                    <div>
                      <h5 className="font-medium text-gray-900 dark:text-white">
                        {example.title}
                      </h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {example.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className={`w-full py-3 px-4 bg-${themeColor}-600 hover:bg-${themeColor}-700 text-white rounded-xl font-medium transition-colors duration-200`}
          >
            {t("common.understood", "Understood")}
          </button>
        </div>
      </div>
    </div>
  );
};
